import { ReactNode } from 'react';
import SelectBoxField from '@/components/select/SelectBoxField';
import { MultiSelectBoxProps } from '@/components/select/selectBoxTypes';
import useMultiSelectBox from '@/components/select/useMultiSelectBox';
import { Listbox } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';

const MultiSelectBox = <T extends { id: string; name: string; disabled?: boolean }>({
  name,
  value,
  label,
  hideLabel,
  deselectLabel,
  queryKey = name ? [name] : [],
  queryFn,
  onSelect,
  allowEmptySelection = true,
  requireSelection,
  disabled = false,
  defaultValue = requireSelection ? 'first' : 'none',
  itemRender,
  hideIfEmpty,
}: MultiSelectBoxProps<T>): ReactNode => {
  const { isLoading, data } = useQuery({ queryKey, queryFn });
  const items = data ?? [];
  const selection = useMultiSelectBox<T>(data, { value, onSelect }, defaultValue);

  if (hideIfEmpty && items.length === 0) return null;

  const displayText = isLoading
    ? 'Loading ...'
    : selection.selected.length > 0
      ? selection.selected.map((item) => item.name).join(', ')
      : deselectLabel || '';

  return (
    <Listbox
      value={selection.selected}
      onChange={selection.handleChange}
      disabled={disabled}
      multiple
    >
      <SelectBoxField<T>
        label={label}
        hideLabel={hideLabel}
        deselectLabel={deselectLabel}
        displayText={displayText}
        itemRender={itemRender}
        items={items}
        allowEmptySelection={allowEmptySelection}
        requireSelection={requireSelection}
        hasNoSelection={selection.selected.length === 0}
        onDeselect={selection.handleDeselect}
      />
    </Listbox>
  );
};

export default MultiSelectBox;
